import { Box, Tooltip } from '@material-ui/core'
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown'
import ArrowDropUpIcon from '@material-ui/icons/ArrowDropUp'
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined'
import { Fragment } from 'react'

import {
  FieldComparison,
  LayerInfo,
} from '@/services/get-infestation-comparison-by-field-from-region'
import { transformUnderscoreInSpaceAndUppercaseFirstLetter } from '@/utils/format-strings'

import { formatDateToBR, formatNumberToBR } from '../../../utils'
import { UnitType } from '../unit-toggle'
import { useStyles } from './styles'

export interface SummaryTableProps {
  field: FieldComparison
  selectedUnit: UnitType
  layerColors: Record<string, string>
  baseDiagnosisDate: string
  compareDiagnosisDate: string
}

export function SummaryTableCompare({
  field,
  selectedUnit,
  layerColors,
  baseDiagnosisDate,
  compareDiagnosisDate,
}: SummaryTableProps) {
  const classes = useStyles()

  function getVariationType(
    value: number,
    baseLayer?: LayerInfo,
    compareLayer?: LayerInfo,
  ) {
    if (!baseLayer || !compareLayer) {
      if (value > 0)
        return {
          type: 'positive',
          class: classes.redTag,
          icon: <ArrowDropUpIcon aria-label='Variação positiva' />,
        }
      if (value < 0)
        return {
          type: 'negative',
          class: classes.greenTag,
          icon: <ArrowDropDownIcon aria-label='Variação negativa' />,
        }
    }

    if (value > 0.01)
      return {
        type: 'positive',
        class: classes.redTag,
        icon: <ArrowDropUpIcon aria-label='Variação positiva' />,
      }
    if (value < -0.01)
      return {
        type: 'negative',
        class: classes.greenTag,
        icon: <ArrowDropDownIcon aria-label='Variação negativa' />,
      }
    return {
      type: 'neutral',
      class: classes.neutralTag,
    }
  }

  const variations = {
    percentage: {
      key: 'pc' as const,
      symbol: '%',
      tooltip:
        'A variação é calculada em pontos percentuais da % de infestação dos diagnósticos.',
      calculateAreaChange: (baseArea: number, compareArea: number) => {
        if (baseArea === 0) return compareArea
        return compareArea - baseArea
      },
      formatVariation: (value: number): string => {
        return `${formatNumberToBR(value)}%`
      },
      formatValueOrDash: (value: number): string => {
        if (!value) return `- ${variations.percentage.symbol}`
        return variations.percentage.formatVariation(value)
      },
    },
    hectares: {
      key: 'ha' as const,
      symbol: ' ha',
      tooltip: 'A variação é calculada em hectares dos diagnósticos.',
      calculateAreaChange: (baseArea: number, compareArea: number) => {
        return compareArea - baseArea
      },
      formatVariation: (value: number): string => {
        return `${formatNumberToBR(value)} ha`
      },
      formatValueOrDash: (value: number): string => {
        if (!value) return `-${variations.hectares.symbol}`
        return variations.hectares.formatVariation(value)
      },
    },
  }

  return (
    <div className={classes.root}>
      <table className={classes.infestTable}>
        <tbody>
          <tr className={classes.headerRow}>
            <th />
            <th></th>
            <th>
              <div className={classes.headerColumn}>
                <span className={classes.headerTitle}>Diag. 1</span>
                <span className={classes.headerSubtitle}>
                  {formatDateToBR(baseDiagnosisDate)}
                </span>
              </div>
            </th>
            <th>
              <div className={classes.headerColumn}>
                <span className={classes.headerTitle}>Diag. 2</span>
                <span className={classes.headerSubtitle}>
                  {formatDateToBR(compareDiagnosisDate)}
                </span>
              </div>
            </th>
            <th>
              <div className={classes.headerColumn}>
                <span className={classes.headerTitle}>Diferença</span>
                <div>
                  <span className={classes.headerSubtitle}>
                    ({variations[selectedUnit].symbol})
                  </span>
                  <Tooltip
                    title={variations[selectedUnit].tooltip}
                    classes={{
                      tooltip: classes.tooltip,
                    }}
                    placement='right-start'
                    PopperProps={{
                      modifiers: {
                        offset: {
                          enabled: true,
                          offset: '-4, -6',
                        },
                      },
                    }}
                  >
                    <InfoOutlinedIcon className={classes.infoIcon} />
                  </Tooltip>
                </div>
              </div>
            </th>
          </tr>

          {field.infestation_groups.map((group) => {
            const currentVariation = variations[selectedUnit]
            const groupChange = currentVariation.calculateAreaChange(
              group.base_diagnosis.area[currentVariation.key],
              group.comparison_diagnosis.area[currentVariation.key],
            )
            const groupVariationType = getVariationType(groupChange)
            const groupName = transformUnderscoreInSpaceAndUppercaseFirstLetter(
              group.name,
            )

            return (
              <Fragment key={group.name}>
                <tr className={classes.groupRow}>
                  <th>&bull;</th>
                  <th>{groupName}</th>
                  <th>
                    {currentVariation.formatValueOrDash(
                      group.base_diagnosis.area[currentVariation.key],
                    )}
                  </th>
                  <th>
                    {currentVariation.formatValueOrDash(
                      group.comparison_diagnosis.area[currentVariation.key],
                    )}
                  </th>
                  <th>
                    <Box className={classes.variationTagContainer}>
                      <Box
                        className={`${classes.variationTag} ${groupVariationType.class}`}
                      >
                        {groupVariationType.icon}
                        <span>
                          {currentVariation.formatVariation(
                            Math.abs(groupChange),
                          )}
                        </span>
                      </Box>
                    </Box>
                  </th>
                </tr>

                {group.base_diagnosis.layers.map((baseLayer) => {
                  const compareLayer = group.comparison_diagnosis.layers.find(
                    (l) => l.name === baseLayer.name,
                  )!

                  const baseValue = baseLayer.area[currentVariation.key]
                  const compareValue = compareLayer.area[currentVariation.key]
                  const layerChange = variations[
                    selectedUnit
                  ].calculateAreaChange(baseValue, compareValue)

                  const layerVariationType = getVariationType(
                    layerChange,
                    baseLayer,
                    compareLayer,
                  )

                  const layerColor = layerColors[baseLayer.name]
                  const layerName =
                    transformUnderscoreInSpaceAndUppercaseFirstLetter(
                      baseLayer.name,
                    )
                  return (
                    <tr key={baseLayer.name}>
                      <td>
                        <Box
                          bgcolor={layerColor}
                          minWidth={16}
                          minHeight={16}
                          maxWidth={16}
                          maxHeight={16}
                          borderRadius={4}
                          marginTop='1px'
                          marginX='auto'
                          title={`Cor da camada ${layerName}`}
                        />
                      </td>
                      <td>
                        <span>{layerName}</span>
                      </td>
                      <td>
                        {currentVariation.formatValueOrDash(
                          baseLayer.area[currentVariation.key],
                        )}
                      </td>
                      <td>
                        {currentVariation.formatValueOrDash(
                          compareLayer.area[currentVariation.key],
                        )}
                      </td>
                      <td>
                        <Box className={classes.variationTagContainer}>
                          <Box
                            className={`${classes.variationTag} ${layerVariationType.class}`}
                          >
                            {layerVariationType.icon}
                            <span>
                              {currentVariation.formatVariation(
                                Math.abs(layerChange),
                              )}
                            </span>
                          </Box>
                        </Box>
                      </td>
                    </tr>
                  )
                })}
              </Fragment>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
